import {
  type IdentityProvider,
  type OrganizationType,
  type SignupSource,
} from "shared-core/types/organization.ts";

export enum Role {
  Admin = "admin",
  Member = "member",
}

export interface UserAuth {
  firstName: string;
  lastName: string;
  role: Role;
  email: string;
  companyName: string;
  companyDomain: string;
  userProfileImage: string;
  organizationStatus: string;
}

export interface EmployeesResponse {
  employees: EmployeeData[];
}

export interface EmployeeData {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string;
  accessRole: Role;
  /** True if MFA is on in Google or Microsoft. */
  twoFactorActive: boolean;
  /** Tri-state: true = on, false = account but MFA off, null = no account in
   * that suite. Suite filters target `=== false`, not `!`. */
  googleMfaActive: boolean | null;
  microsoftMfaActive: boolean | null;
  /** From the account rows, not the MFA tri-state: legacy rows can hold a null there. */
  hasDirectoryAccount: boolean;
  userStatus: UserStatus;
  joinedDate: string | null;
  cyberscore: number;
  profileImageUrl: string | null;
  agentVersion: string | null;
  lastReminderSent: string | null;
  isExternallyManaged: boolean;
  externalManagementProviders: IdentityProvider[];
  canWithdrawInvitation: boolean;
  deactivatedAt: string | null;
  /** ISO timestamp, null while permanent. */
  reactivateAt: string | null;
}

export enum UserStatus {
  CREATED = "created", // Don't forget to change Agent's default if removing/changing this!
  ACTIVE = "active",
  INVITED = "invited",
  INACTIVE = "inactive",
}

export enum AdminUserErrorReason {
  CANNOT_DELETE_SELF = "admin_user_errors.cannot_delete_self",
  CANNOT_DELETE_ACTIVE_USER = "admin_user_errors.cannot_delete_active_user",
  CANNOT_DELETE_SYNCED_USER = "admin_user_errors.cannot_delete_synced_user",
  CANNOT_WITHDRAW_NON_INVITED_USER = "admin_user_errors.cannot_withdraw_non_invited_user",
  CANNOT_WITHDRAW_UNSYNCED_USER = "admin_user_errors.cannot_withdraw_unsynced_user",
  CANNOT_WITHDRAW_WHILE_AUTO_INVITE_ENABLED = "admin_user_errors.cannot_withdraw_while_auto_invite_enabled",
  CANNOT_DEACTIVATE_SELF = "admin_user_errors.cannot_deactivate_self",
  CANNOT_RESTORE_SELF = "admin_user_errors.cannot_restore_self",
  CANNOT_DEMOTE_SELF = "admin_user_errors.cannot_demote_self",
  USER_NOT_FOUND = "admin_user_errors.user_not_found",
  ALREADY_INACTIVE = "admin_user_errors.already_inactive",
  NOT_DEACTIVATED = "admin_user_errors.not_deactivated",
  RESTORE_BLOCKED_BY_INTEGRATION = "admin_user_errors.restore_blocked_by_integration",
  REACTIVATION_DATE_NOT_IN_FUTURE = "admin_user_errors.reactivation_date_not_in_future",
}

export interface UserDataResponse {
  userData: UserData;
}

export interface UserData {
  id: number;
  firstName: string | null;
  lastName: string | null;
  role: Role;
  email: string;
  companyName: string | null;
  companyDomain: string;
  profileImageUrl: string | null;
  organizationStatus: string;
  hasRegistered: boolean;
  lastUsedProvider: IdentityProvider;
  signupSource: SignupSource;
  googleWorkspaceEmail: string | null;
  organizationId: number;
  crispSignature: string | null;
  organizationType: OrganizationType;
  deleteAt: string | null;
  organizationDeleteAt: string | null;
}

export interface StaleUserData {
  id: string;
  primaryEmail: string | null;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
  lastLoginTime: string | null;
  profileImageUrl: string | null;
}

export interface StaleUserResponse {
  staleUsers: StaleUserListItem[];
  lastUpdated: string | null;
}

export enum StaleUserActionState {
  UNASSIGNED = "unassigned",
  DELETE = "delete",
  SUSPEND = "suspend",
  STALE_SKIPPED = "stale_skipped",
}

export interface StaleUserListItem extends StaleUserData {
  state: StaleUserActionState;
}

export interface StaleUserActionRequest {
  userId: string;
  action: StaleUserActionState;
}

export interface StaleUserApplyRequest {
  actions: StaleUserActionRequest[];
}

export interface StaleUserApplyResponse {
  applied: StaleUserActionRequest[];
}

export interface MicrosoftStaleUserResponse {
  staleUsers: StaleUserListItem[] | null;
  lastUpdated: string | null;
}

export interface SuperAdminCountResponse {
  count: number;
}
